import { useFrame, useThree } from '@react-three/fiber'
import { calculateCameraOffset } from 'features/kestrelLite/utils/cameraPositioning'
import { FC, useEffect, useMemo, useRef } from 'react'
import { Vector3 } from 'three'

/**
 * Component that animates camera to fly to a target position
 */
export const FlyToTarget: FC<{
  target: Vector3 | null
  targetSize?: { width: number; depth: number }
  topDownView?: boolean
  angleOffVerticalDegrees?: number
  onComplete: () => void
}> = ({
  target,
  targetSize,
  topDownView,
  angleOffVerticalDegrees = 15,
  onComplete,
}) => {
  const { camera, controls, size } = useThree()
  const isFlying = useRef(false)
  const startPosition = useRef(new Vector3())
  const startTarget = useRef(new Vector3())
  const progress = useRef(0)

  const aspectRatio = useMemo(() => size.width / size.height, [size])

  // Reusable vectors for frame loop calculations
  const tempVectors = useMemo(
    () => ({
      offset: new Vector3(),
      endPosition: new Vector3(),
    }),
    []
  )

  useEffect(() => {
    if (target && !isFlying.current) {
      isFlying.current = true
      progress.current = 0
      startPosition.current.copy(camera.position)

      // Get current orbit target if available
      // OrbitControls type is not exported, so we cast to access the target property
      const orbitControls = controls as { target?: Vector3 }
      if (orbitControls?.target) {
        startTarget.current.copy(orbitControls.target)
      } else {
        startTarget.current.set(0, 0, 0)
      }
    }
  }, [target, camera, controls])

  useFrame((_, delta) => {
    if (!target || !isFlying.current) return

    progress.current += delta * 1.5
    const t = Math.min(progress.current, 1)
    const eased = 1 - Math.pow(1 - t, 3)

    const cameraFov =
      'fov' in camera && typeof camera.fov === 'number' ? camera.fov : 60

    const defaultSize = { width: 100, depth: 100 }
    const effectiveSize = targetSize || defaultSize

    const offset = calculateCameraOffset({
      targetSize: effectiveSize,
      fov: cameraFov,
      aspectRatio,
      paddingFactor: topDownView ? 1.2 : 1.5,
      topDownView,
      angleOffVerticalDegrees,
    })

    tempVectors.offset.set(offset.x, offset.y, offset.z)

    tempVectors.endPosition.copy(target).add(tempVectors.offset)
    camera.position.lerpVectors(
      startPosition.current,
      tempVectors.endPosition,
      eased
    )

    // OrbitControls type is not exported, so we cast to access target and update methods
    const orbitControls = controls as { target?: Vector3; update?: () => void }
    if (orbitControls?.target) {
      orbitControls.target.lerpVectors(startTarget.current, target, eased)
      orbitControls.update?.()
    }

    if (t >= 1) {
      isFlying.current = false
      onComplete()
    }
  })

  return null
}
